import * as React from 'react';
import { useState, useCallback } from 'react';
import URI from 'urijs';

import { Reports } from '@graylog/enterprise-api';

import type { Report, ReportSummary } from 'report/types';
import useSendTelemetry from 'logic/telemetry/useSendTelemetry';
import { TELEMETRY_EVENT_TYPE } from 'telemetry/Constants';
import { MenuItem, DeleteMenuItem } from 'components/bootstrap';
import ButtonToolbar from 'components/bootstrap/ButtonToolbar';
import useSelectedEntities from 'components/common/EntityDataTable/hooks/useSelectedEntities';
import UserNotification from 'util/UserNotification';
import { HoverForHelp, ShareButton, IfPermitted } from 'components/common';
import { reportingUrl } from 'report/Constants';
import { MoreActions } from 'components/common/EntityDataTable';
import ReportRoutes from 'report/ReportRoutes';
import useTableFetchContext from 'components/common/PaginatedEntityTable/useTableFetchContext';
import EntityShareModal from 'components/permissions/EntityShareModal';
import usePluggableEntitySharedActions from 'hooks/usePluggableEntitySharedActions';
import useScopePermissions from 'hooks/useScopePermissions';

const downloadUrl = (reportId: string, reportTitle: string) =>
  new URI(reportingUrl(encodeURI(`${reportId}/generate/${reportTitle}`))).toString();

const _reportHasRecipients = (delivery: Report['delivery']) =>
  delivery &&
  ((Array.isArray(delivery.user_receivers) && delivery.user_receivers.length > 0) ||
    (Array.isArray(delivery.email_receivers) && delivery.email_receivers.length > 0));

const SendReportMenuItem = ({
  reportDelivery,
  reportId,
  reportTitle,
}: {
  reportDelivery: Report['delivery'];
  reportId: string;
  reportTitle: string;
}) => {
  const sendTelemetry = useSendTelemetry();
  const reportHasRecipients = _reportHasRecipients(reportDelivery);
  const isDisabled = !reportHasRecipients;

  const onSendReport = async () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.REPORT.SENT, {
      app_pathname: 'report',
      app_section: 'report-form',
    });

    await Reports.sendReport(reportId)
      .then(() => {
        UserNotification.success(
          'The Report is being generated and will be send to all recipients shortly, check your inbox in a few minutes.',
          'Report delivery scheduled',
        );
      })
      .catch(() => {
        UserNotification.error(
          `Sending Report "${reportTitle}" email failed. Please check your server logs for more information.`,
          'Could not send Report',
        );
      });
  };

  return (
    <MenuItem disabled={isDisabled} onSelect={onSendReport}>
      Send report now
      {isDisabled && (
        <HoverForHelp trigger={['hover', 'click']} placement="left" displayLeftMargin>
          Sending report disabled, add recipients first
        </HoverForHelp>
      )}
    </MenuItem>
  );
};

const DeleteReportAction = ({ reportId, reportTitle }: { reportId: string; reportTitle: string }) => {
  const { deselectEntity } = useSelectedEntities();
  const { refetch } = useTableFetchContext();
  const sendTelemetry = useSendTelemetry();

  const onDeleteReport = async () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.REPORT.DELETED, {
      app_pathname: 'report',
      app_section: 'reports-overview',
    });

    // eslint-disable-next-line no-alert
    if (window.confirm(`Do you really want to delete report "${reportTitle}"? This action cannot be undone.`)) {
      await Reports.remove(reportId)
        .then(() => {
          deselectEntity(reportId);
          UserNotification.success(`Report "${reportTitle}" was deleted successfully.`, 'Success');
          refetch();
        })
        .catch((error) => {
          UserNotification.error(`Deleting Report "${reportTitle}" failed with status: ${error}`);
        });
    }
  };

  return <DeleteMenuItem onSelect={onDeleteReport} />;
};

type Props = {
  report: ReportSummary;
};

const ReportActions = ({ report }: Props) => {
  const [showReportShareModal, setShowReportShareModal] = useState<boolean>(false);
  const { actions: pluggableActions, actionModals: pluggableActionModals } = usePluggableEntitySharedActions<
    Props['report']
  >(report, 'report');
  const moreActions = [pluggableActions.length ? pluggableActions : null].filter(Boolean);
  const { scopePermissions } = useScopePermissions(report);
  const scopeDeletable = scopePermissions.is_deletable !== false;

  const toggleShareModalOpen = useCallback(() => {
    setShowReportShareModal((cur) => !cur);
  }, [setShowReportShareModal]);

  const sendTelemetry = useSendTelemetry();

  const onDownload = () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.REPORT.DOWNLOAD, {
      app_pathname: 'report',
      app_section: 'reports-overview',
    });
  };

  return (
    <ButtonToolbar>
      <ShareButton entityType="report" entityId={report.id} onClick={toggleShareModalOpen} bsSize="xsmall" />
      <MoreActions>
        <MenuItem href={ReportRoutes.contents(report.id)}>Edit</MenuItem>
        <MenuItem href={ReportRoutes.archive(report.id)}>Report archive</MenuItem>
        <MenuItem href={downloadUrl(report.id, report.title)} target="_blank" onClick={onDownload}>
          Download report now
        </MenuItem>
        <SendReportMenuItem reportDelivery={report.delivery} reportTitle={report.title} reportId={report.id} />
        {moreActions}
        {scopeDeletable && (
          <IfPermitted permissions={`report:delete:${report.id}`}>
            <MenuItem divider />
            <DeleteReportAction reportId={report.id} reportTitle={report.title} />
          </IfPermitted>
        )}
      </MoreActions>
      {showReportShareModal && (
        <EntityShareModal
          entityId={report.id}
          entityType="report"
          entityTitle={report.title}
          description="Search for a User or Team to add as collaborator on this report."
          onClose={toggleShareModalOpen}
        />
      )}
      {pluggableActionModals}
    </ButtonToolbar>
  );
};

export default ReportActions;
